import { useEffect, useState } from "react";

type ConsentValue = "granted" | "denied";
type ConsentChoice = {
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  analytics_storage: ConsentValue;
};

const STORAGE_KEY = "pol_consent_v1";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function pushConsent(choice: ConsentChoice) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  const gtag = window.gtag || ((...args: unknown[]) => { window.dataLayer!.push(args); });
  window.gtag = gtag;
  gtag("consent", "update", choice);
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  function save(choice: ConsentChoice) {
    pushConsent(choice);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, ts: Date.now() })); } catch {}
    setVisible(false);
  }

  function acceptAll() {
    save({ ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted" });
  }
  function rejectAll() {
    save({ ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
  }
  function savePrefs() {
    save({
      ad_storage: ads ? "granted" : "denied",
      ad_user_data: ads ? "granted" : "denied",
      ad_personalization: ads ? "granted" : "denied",
      analytics_storage: analytics ? "granted" : "denied",
    });
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] px-3 pb-3 sm:px-4 sm:pb-4">
      <div className="mx-auto max-h-[75dvh] max-w-4xl overflow-y-auto rounded-lg border border-border bg-card p-4 text-card-foreground shadow-[var(--shadow-elegant)] sm:p-5 md:p-6">
        {!showPrefs ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="flex-1 text-[13px] leading-relaxed sm:text-sm">
              <p className="font-semibold text-foreground mb-1">We value your privacy</p>
              <p className="text-muted-foreground">
                We use cookies to analyze traffic and improve your experience. You can accept all, reject all, or customize your choices. See our{" "}
                <a href="/privacy" className="underline hover:text-foreground">privacy policy</a>.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:justify-end">
              <button onClick={() => setShowPrefs(true)} className="min-h-11 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent sm:px-4">Customize</button>
              <button onClick={rejectAll} className="min-h-11 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent sm:px-4">Reject all</button>
              <button onClick={acceptAll} className="col-span-2 min-h-11 rounded-md bg-[var(--gold)] px-4 py-2 text-sm font-semibold text-[var(--navy-deep)] hover:bg-[var(--gold-deep)] md:col-span-1">Accept all</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-foreground">Cookie preferences</p>
              <p className="text-sm text-muted-foreground">Essential cookies are always on. Choose what else we may use.</p>
            </div>
            <div className="space-y-3">
              <Row title="Essential" desc="Required for the site to function." checked disabled />
              <Row title="Analytics" desc="Helps us understand how visitors use the site." checked={analytics} onChange={setAnalytics} />
              <Row title="Advertising" desc="Used to measure and personalize ads." checked={ads} onChange={setAds} />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={() => setShowPrefs(false)} className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent">Back</button>
              <button onClick={savePrefs} className="rounded-md bg-[var(--gold)] hover:bg-[var(--gold-deep)] text-[var(--navy-deep)] px-4 py-2 text-sm font-semibold">Save preferences</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ title, desc, checked, onChange, disabled }: { title: string; desc: string; checked: boolean; onChange?: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className={`flex items-start justify-between gap-4 rounded-md border border-border p-3 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
      <div>
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} className="mt-1 h-4 w-4 accent-[var(--gold-deep)]" />
    </label>
  );
}

export function openConsentPreferences() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
  if (typeof window !== "undefined") window.location.reload();
}